import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '../services/amplifyCompat';
import { getActionItemsForAction } from '../graphql/queries';
import { getSimpleActionItemFromResponse } from '../utils/get-simple-action-item-from-response';
import { useContentTranslationsContext } from '../components/contexts/ContentTranslationsContext';
import { useLanguageContext } from '../components/contexts/LanguageContext';
import { useGetSingleAction } from './use-get-single-action';

export const useGetItemsForAction = () => {
  const params = useParams();
  const { slugId, slugError, isLoading: isSlugLoading } = useGetSingleAction();
  const rawActionId = params?.actionId || null;
  const isSlug = rawActionId && isNaN(Number(rawActionId));
  // When the URL contains a slug, wait for slug→ID resolution before querying.
  // paramsActionId is null until slugId resolves, preventing invalid GraphQL queries.
  const paramsActionId = isSlug ? slugId : rawActionId;
  const { language } = useLanguageContext();
  const { contentTranslations } = useContentTranslationsContext();
  const [actionId, setActionId] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [hasNoItems, setHasNoItems] = useState(false);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [error, setError] = useState(null);

  const translateActions = useCallback((actions, languageCode) => {
    const translations = contentTranslations?.find(
      (translation) => translation.langCode === languageCode
    );
    if (!translations || !translations.translationJSON?.actions) {
      return actions;
    }
    return actions.map((action) => {
      const translatedAction = translations.translationJSON.actions.find(
        (translation) => translation.action_id === action.action_id
      );
      return translatedAction ? { ...action, ...translatedAction } : action;
    });
  }, [contentTranslations]);

  const formatUpdatedActions = (translatedActions) => {
    return translatedActions.reduce((acc, item) => {
      const updateActionItems = item.action_items.map((subItem, index) => ({
        actionId: subItem.action_id,
        id: `${subItem.action_id}-${index}`,
        name: subItem.item_name,
        name_en: translatedActions[subItem.index].item_name,
        description: subItem.item_description,
        co2Saved: subItem?.co2_saved_per_unit || 0,
      }));
      updateActionItems.forEach((subItem) => {
        if (!acc.some((existingItem) => existingItem.id === subItem.id)) {
          acc.push(subItem);
        }
      });
      return acc;
    }, []);
  };

  const fetchItemsForAction = useCallback(async () => {
    setIsItemsLoading(true);
    setError(null);

    try {
      const response = await API.graphql({
        query: getActionItemsForAction,
        variables: { action_id: paramsActionId },
      });

      const items = response?.data?.getActionItemsForAction || [];
      setActionId(paramsActionId);
      setHasNoItems(items.length === 0);
      setIsItemsLoading(false);

      let updatedItems;
      if (language === 'fr') {
        const translatedActions = translateActions(items, 'fr');
        updatedItems = formatUpdatedActions(translatedActions);
      } else if (language === 'es') {
        const translatedActions = translateActions(items, 'es');
        updatedItems = formatUpdatedActions(translatedActions);
      } else {
        updatedItems = items.map(getSimpleActionItemFromResponse);
      }

      setActionItems(updatedItems);
    } catch (e) {
      setError(e);
      setIsItemsLoading(false);
    }
  }, [paramsActionId, language, translateActions]);

  useEffect(() => {
    if (paramsActionId) {
      setActionItems([]);
      fetchItemsForAction();
    }
  }, [paramsActionId, fetchItemsForAction]);

  useEffect(() => {
    if (paramsActionId && paramsActionId !== actionId) {
      setHasNoItems(false);
      setActionItems([]);
    }
  }, [paramsActionId, actionId]);

  useEffect(() => {
    if (slugError) {
      setError(slugError);
      setIsItemsLoading(false);
    }
  }, [slugError]);

  return {
    actionItems,
    hasNoItems,
    loading: isSlugLoading || isItemsLoading,
    error,
  };
};
